import { billingStore } from '@stores/Billing';
import { notificationStore } from '@stores/Notifications';
import { isReachableWhileWalled, paywallStore } from '@stores/Paywall';
import { postAuthRedirectStore } from '@stores/PostAuthRedirect';
import { trackingStore } from '@stores/Tracking';
import { uiChromeStore } from '@stores/UIChrome';
import { userStore } from '@stores/User';
import { useEffect, useRef, useState } from 'react';

import { MobileNotifCenter } from '@components/MobileNotifCenter/MobileNotifCenter';
import {
  AppBootstrapError,
  AppBootstrapLoading,
} from '@components/util/AppBootstrapFallback/AppBootstrapFallback';
import { ErrorCatcher } from '@components/util/ErrorBoundary';
import { Z_INDEX } from '@constants/z-index';
import { useIsMobile } from '@lib/hooks/sizing/useIsMobile';
import { useSocialConnectReturn } from '@lib/hooks/useSocialConnectReturn';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Alert, AlertTitle, Button, Tooltip } from '@mui/material';
import { AuthBox } from '@pages/Auth/AuthBox';
import { PaywallBar } from '@pages/Paywall/PaywallBar';
import { serviceWorkerService } from '@services/ServiceWorkerService';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Toaster } from 'sonner';
import css from './App.module.scss';
import { MobileNav } from './components/Nav/MobileNav';
import { DesktopNav } from './components/Nav/Nav';

export const App = observer(function App() {
  const navigate = useNavigate();
  const location = useLocation();

  // Track page views on route change
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (location.pathname !== prevPathRef.current) {
      trackingStore.pageView(location.pathname);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  // Track initial page view
  useEffect(() => {
    trackingStore.pageView(location.pathname);
  }, []);

  // Check if billing data is stale on app start
  useEffect(() => {
    if (userStore.isLoggedIn && billingStore.isBillingDataStale()) {
      console.log('Billing data is stale (>1 minute old), refreshing...');
      billingStore
        .refetchCreditsAfterOperation()
        .then(() => {
          // Broadcast to other tabs to read from localStorage
          serviceWorkerService.postMessage({
            type: 'REFRESH_CREDITS',
          });
        })
        .catch((error) => {
          console.error('Failed to refresh stale billing data:', error);
        });
    }
  }, []); // Run once on mount

  // Restore persisted media notifications once the user ID is known. Re-runs
  // if the user switches in-place (logout + login as a different account)
  // so the new user only sees their own notifications. setCurrentUser scopes
  // the storage key by user ID, preventing cross-account leakage.
  const currentUserId = userStore.data?._id;
  useEffect(() => {
    if (userStore.isLoggedIn && currentUserId) {
      notificationStore.setCurrentUser(currentUserId);
      notificationStore.restoreFromStorage();
    }
  }, [currentUserId]);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'REFRESH_ACCOUNT_DETAILS') {
          console.log('REFRESH_ACCOUNT_DETAILS');
          const wasLoggedIn = userStore.isLoggedIn;
          const wasVerified = userStore.data?.isVerified;
          const localToken = localStorage.getItem('token');
          userStore.setToken(localToken);
          if (!localToken && wasLoggedIn) {
            // Clear billing data when user logs out
            billingStore.data = null;
            billingStore.clearBillingFromLocalStorage();
            navigate('/login');
            return;
          }
          userStore.fetch();
          // Update billing store when user logs in
          if (localToken) {
            billingStore.refetchCreditsAfterOperation();
          }
          if (localToken && !userStore.data?.isVerified) {
            navigate('/verify-email');
            return;
          }
          if (!wasVerified && userStore.data?.isVerified) {
            navigate('/');
            return;
          }
        } else if (event.data && event.data.type === 'REFRESH_CREDITS') {
          console.log('REFRESH_CREDITS');
          // Load credits from localStorage (updated by the tab that hit the API)
          billingStore.loadBillingFromLocalStorage();
        }
      });
    }
  }, []);

  // Check for refresh_credits query parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('refresh_credits')) {
      // Only refresh if user is logged in
      if (userStore.isLoggedIn) {
        console.log('refresh_credits parameter detected - refreshing credits');

        // Trigger credit refresh (hits API and saves to localStorage)
        billingStore
          .refetchCreditsAfterOperation()
          .then(() => {
            // Broadcast to other tabs to read from localStorage
            serviceWorkerService.postMessage({
              type: 'REFRESH_CREDITS',
            });
          })
          .catch((error) => {
            console.error('Failed to refresh credits:', error);
          });
      }

      // Remove the parameter from URL
      searchParams.delete('refresh_credits');
      const newSearch = searchParams.toString();
      const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}${location.hash}`;
      navigate(newUrl, { replace: true });
    }
  }, [location.search]);

  return (
    <ErrorCatcher showDialog>
      <div className={css.main}>
        <div className={css.content}>
          <Outlet />
        </div>
        {Boolean(userStore.isImpersonating) && (
          <div className={css.stripedBorder}>
            <div className={css.topBorder}></div>
            <div className={css.bottomBorder}></div>
            <div className={css.leftBorder}></div>
            <div className={css.rightBorder}></div>
          </div>
        )}
      </div>
    </ErrorCatcher>
  );
});

export const WithNav = observer(function WithNav() {
  const isMobile = useIsMobile();
  // A social connect comes back to whichever page under this shell started it.
  useSocialConnectReturn();

  if (!(userStore.data?.isVerified ?? true)) {
    return null;
  }

  // Behind the paywall the only pages under this shell are the account and billing
  // settings; they get the wall's own bar instead of a sidebar full of places the
  // user can't go.
  if (paywallStore.isWalled) {
    return (
      <div className={css.walledShell}>
        <PaywallBar />
        <Outlet />
      </div>
    );
  }

  return (
    <>
      {isMobile ? <MobileNav /> : <DesktopNav />}
      <Outlet />
      {/* Mobile unified notifications/uploads center: a fixed bell FAB + drawer.
          Mounted at the shell root (like MobileNav) so its `position: fixed`
          isn't trapped by a page's transformed / backdrop-filtered ancestors. */}
      {isMobile && <MobileNotifCenter />}
    </>
  );
});

export const WithAuthHero = observer(function WithAuthHero() {
  return (
    <>
      <AuthBox>
        <Outlet />
      </AuthBox>
    </>
  );
});

export const WithUser = observer(function WithUser() {
  const navigate = useNavigate();
  const location = useLocation();
  const [hideImpersonatingText, setHideImpersonatingText] = useState(false);

  // Prefetch billing post-auth regardless of which route we land on — the
  // billing-dependent UI (credits, paywalls) and the render gate below need
  // it. Deliberately NOT a condition for post-auth redirects: those fire on
  // the verified session alone, so a failed billing fetch can't strand them.
  useEffect(() => {
    if (!userStore.data?.isVerified) return;
    if (billingStore.data && !billingStore.isBillingDataStale()) return;
    billingStore.fetch().catch((error) => {
      console.error('WithUser: failed to prefetch billing data:', error);
    });
  }, [userStore.data?.isVerified]);

  useEffect(() => {
    if (!userStore.isLoggedIn) {
      // Store the current path for redirect after login.
      const currentPath = window.location.pathname + window.location.search + window.location.hash;
      // Only store if it's not a login/verify path to prevent redirect loops
      if (!currentPath.startsWith('/login') && !currentPath.startsWith('/verify-email')) {
        sessionStorage.setItem('redirectAfterAuth', currentPath);
      }
      navigate('/login');
    } else if (userStore.data && !userStore.data?.isVerified) {
      // Email verification gates everything, including a pending external redirect:
      // unverified users go to /verify-email first. The redirect target is kept in
      // the durable sessionStorage stash (see userStore), so once they verify —
      // this effect re-runs with isVerified true and the branch below sends them on.
      const verifyUrl = userStore.data.email
        ? `/verify-email?email=${encodeURIComponent(userStore.data.email)}`
        : '/verify-email';
      navigate(verifyUrl);
    } else if (userStore.data?.isVerified) {
      // Verified: NOW honor any pending redirect, in priority order. (No billing
      // condition: redirects fire on the verified session alone, so a failed
      // billing fetch can't strand them, and the paywall below never holds a
      // login bounce to another app hostage — each app walls itself. An
      // internal deep link is navigated to and then walled, so a free user
      // loses it; the workspace is where they land once they subscribe.
      // postAuthRedirectStore's own reaction usually beats this effect to it;
      // this call is an idempotent backstop.)
      //
      // An EXPLICIT redirect the user arrived with takes precedence over the
      // durable post-signup fallback: first a `?redirect=` target owned by
      // postAuthRedirectStore (how chat/agents.starzero.ai bounce through
      // login), then a stored internal deep link (redirectAfterAuth). The
      // post-signup destination — remembered from a marketing-landing
      // `.starzero.ai` cookie persisted to user settings — is only the
      // FALLBACK for when no explicit redirect survived the magic-link
      // round-trip / reloads, so it's checked LAST. Checking it first would
      // let a shared-domain cookie hijack another app's login bounce. When an
      // explicit redirect wins, consume the fallback so it can't re-fire to a
      // stale target on a later visit. (Verification gates all of this via
      // the branch above; the durable stashes kept the targets through it.)
      if (postAuthRedirectStore.hasPendingRedirect()) {
        void postAuthRedirectStore.clearPostOnboardingRedirect();
        void postAuthRedirectStore.redirectIfRequested();
        return;
      }
      const redirectPath = sessionStorage.getItem('redirectAfterAuth');
      if (redirectPath) {
        try {
          // Parse the URL to properly validate it
          const url = new URL(redirectPath, window.location.origin);

          // Only allow paths that:
          // 1. Are on the same origin
          // 2. Start with a single forward slash
          // 3. Don't contain any special characters or path traversal attempts
          if (
            url.origin === window.location.origin &&
            url.pathname.startsWith('/') &&
            !url.pathname.includes('//') &&
            !url.pathname.includes('..') &&
            !url.pathname.includes('\\')
          ) {
            sessionStorage.removeItem('redirectAfterAuth');
            void postAuthRedirectStore.clearPostOnboardingRedirect();
            navigate(url.pathname + url.search + url.hash);
          } else {
            navigate('/workspace');
          }
        } catch (e) {
          // If URL parsing fails, default to workspace
          navigate('/workspace');
        }
        return;
      }

      // No explicit redirect pending — honor a remembered post-signup
      // destination from the marketing landing, once (it clears itself).
      if (postAuthRedirectStore.pendingPostOnboardingRedirect) {
        void postAuthRedirectStore.honorPostOnboardingRedirect();
        return;
      }
    }
  }, [
    userStore.isLoggedIn,
    userStore.data?.isVerified,
    postAuthRedirectStore.pendingPostOnboardingRedirect,
  ]);

  // Once authenticated, persist any landing-set post-signup redirect cookie
  // to user settings (durable from here, see PostAuthRedirectStore). Keyed on
  // the user id so it runs once per session; the cookie is consumed on first
  // read.
  useEffect(() => {
    if (userStore.data?._id) {
      void postAuthRedirectStore.capturePostOnboardingRedirect();
    }
  }, [userStore.data?._id]);

  if (!userStore.data) {
    // Bootstrapping the session (fetching /user/me). While still logged in (token
    // present), show a branded loader — or, if the bootstrap hit a connectivity
    // error (e.g. DNS not ready right after wake-from-sleep, which can hang ~30s),
    // a retry — instead of a black screen. If the token was cleared (genuine
    // session expiry / server 401), isLoggedIn is false and the effect above
    // redirects to /login, so render nothing here.
    if (userStore.isLoggedIn) {
      return userStore.error ? (
        <AppBootstrapError onRetry={() => window.location.reload()} />
      ) : (
        <AppBootstrapLoading />
      );
    }
    return null;
  }
  // The paywall gate. Unverified users are handled by the effect above
  // (redirect to /verify-email) and have no billing document to read.
  if (userStore.data.isVerified) {
    // Wait for billing to settle: the verdict needs it.
    if (!billingStore.data && !billingStore.error) {
      return <AppBootstrapLoading />;
    }
    // Billing failed and /user/me can't vouch for a subscription: neither the
    // app nor the wall is a safe guess, so ask for a retry instead of either.
    if (paywallStore.isUndecidable) {
      return <AppBootstrapError onRetry={() => window.location.reload()} />;
    }
    // The store latches its verdict in the same action that lands billing
    // data; this only shows between the two if something delays it.
    if (paywallStore.access === null) {
      return <AppBootstrapLoading />;
    }
    if (paywallStore.isWalled && !isReachableWhileWalled(location.pathname)) {
      return <Navigate to="/subscribe" replace />;
    }
  }

  return (
    <>
      {userStore.isImpersonating && (
        <Alert
          severity="warning"
          className={classNames(css.impersonating, { [css.hidden]: hideImpersonatingText })}
          variant="filled"
          icon={<VisibilityIcon />}
          action={
            !hideImpersonatingText && (
              <Tooltip title="Hide Text">
                <Button onClick={() => setHideImpersonatingText(true)}>
                  <CloseIcon />
                </Button>
              </Tooltip>
            )
          }
        >
          {!hideImpersonatingText && (
            <>
              <AlertTitle>Proceed with caution, this is not your account</AlertTitle>
              <span>
                You are currently logged in as <b>{userStore.data?.email}</b> with{' '}
                <b>FULL CONTROL</b>
              </span>
              <span>
                Token expires in{' '}
                {Math.round(((userStore.tokenData?.exp ?? 0) - Date.now() / 1000) / 60)} minutes
              </span>
            </>
          )}
        </Alert>
      )}
      <Outlet />
    </>
  );
});

export const WithoutUser = observer(function WithoutUser() {
  const navigate = useNavigate();

  useEffect(() => {
    if (userStore.isLoggedIn) {
      navigate('/' + window.location.search + window.location.hash);
    }
  }, [userStore.isLoggedIn]);

  return <Outlet />;
});

export const WithToaster = observer(function WithToaster({
  children,
}: {
  children: React.ReactNode;
}) {
  const isMobile = useIsMobile();

  // Sonner renders an empty <section> sibling to the <ol> that holds the
  // toasts. Sonner's CSS only targets the inner <ol> ([data-sonner-toaster]),
  // so the empty section is a default-flow block element that consumes full
  // page width when no toasts are visible and shifts the page layout. Wrap
  // it in a fixed-positioned, pointer-event-transparent div so the section
  // is out of normal flow regardless of how many toasts are active.
  //
  // The z-index is load-bearing and lives on THIS div, not on sonner's own element: a
  // `position: fixed` box forms a stacking context, so sonner's internal z-index is
  // clamped inside whatever this wrapper is given. `Z_INDEX.toast` documents where that
  // sits and why.
  const wrapperStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none',
    zIndex: Z_INDEX.toast,
  };

  // Mobile: keep the existing top-center placement until we revisit mobile
  // notification UX. No SearchBar tracking — top is a fixed anchor.
  if (isMobile) {
    return (
      <>
        <div style={wrapperStyle}>
          <Toaster position="top-center" />
        </div>
        {children}
      </>
    );
  }

  // Desktop: bottom-center by default, pinned to the SearchBar's left edge
  // when the bar is mounted (Search/Workspace) so the stack aligns with the
  // content area instead of the raw viewport. uiChromeStore computes both
  // axes from the bar's measured rect; sonner's [data-sonner-toaster] gets
  // `transition: bottom, left` in App.module.scss so the position animates
  // smoothly with the bar.
  const bottomOffset = uiChromeStore.toastOffsetPx;
  const leftOffset = uiChromeStore.toastLeftPx;

  return (
    <>
      <div style={wrapperStyle}>
        {leftOffset != null ? (
          <Toaster position="bottom-left" offset={{ bottom: bottomOffset, left: leftOffset }} />
        ) : (
          <Toaster position="bottom-center" offset={bottomOffset} />
        )}
      </div>
      {children}
    </>
  );
});
